"use client";

import { useCallback, useEffect, useState, type RefCallback } from "react";

import { findEffectiveScrollRoot } from "../utils/findEffectiveScrollRoot";

type UseIntersectionObserverOptions = Omit<IntersectionObserverInit, "root"> & {
  root?: Element | Document | null;
};

type UseIntersectionObserverResult<T extends Element> = readonly [
  ref: RefCallback<T>,
  entry: IntersectionObserverEntry | null,
];

type ObservedEntry<T extends Element> = {
  entry: IntersectionObserverEntry;
  target: T;
  root: UseIntersectionObserverOptions["root"];
  rootMargin: string;
  thresholdKey: string;
};

const canUseIntersectionObserver = () =>
  typeof IntersectionObserver !== "undefined";

const serializeThreshold = (
  threshold: IntersectionObserverInit["threshold"] = 0,
): string => {
  if (Array.isArray(threshold)) {
    return threshold.join(",");
  }

  return String(threshold);
};

const parseThreshold = (
  thresholdKey: string,
): IntersectionObserverInit["threshold"] => {
  if (thresholdKey.includes(",")) {
    return thresholdKey.split(",").map(Number);
  }

  return Number(thresholdKey);
};

const useIntersectionObserver = <T extends Element>({
  root,
  rootMargin = "0px",
  threshold = 0,
}: UseIntersectionObserverOptions = {}): UseIntersectionObserverResult<T> => {
  const [node, setNode] = useState<T | null>(null);
  const [observedEntry, setObservedEntry] = useState<ObservedEntry<T> | null>(
    null,
  );

  const thresholdKey = serializeThreshold(threshold);
  const isSupported = canUseIntersectionObserver();

  const ref = useCallback<RefCallback<T>>((nextNode) => {
    setNode(nextNode);
  }, []);

  useEffect(() => {
    if (!node || !isSupported) {
      return;
    }

    const observerRoot = root ?? findEffectiveScrollRoot(node);
    let didDisconnect = false;

    const observer = new IntersectionObserver(
      ([nextEntry]) => {
        if (didDisconnect || !nextEntry) {
          return;
        }

        setObservedEntry({
          entry: nextEntry,
          target: node,
          root,
          rootMargin,
          thresholdKey,
        });
      },
      {
        root: observerRoot,
        rootMargin,
        threshold: parseThreshold(thresholdKey),
      },
    );

    observer.observe(node);

    return () => {
      didDisconnect = true;
      observer.disconnect();
    };
  }, [isSupported, node, root, rootMargin, thresholdKey]);

  const entry = (() => {
    if (!isSupported) return null;
    if (observedEntry?.target !== node) return null;
    if (observedEntry.root !== root) return null;
    if (observedEntry.rootMargin !== rootMargin) return null;
    if (observedEntry.thresholdKey !== thresholdKey) return null;

    return observedEntry.entry;
  })();

  return [ref, entry] as const;
};

export { useIntersectionObserver };
export type { UseIntersectionObserverOptions, UseIntersectionObserverResult };
